import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCategories } from "../utils/useCategories";
import { useWishlist } from "../context/WishlistContext";
import { useLanguage } from "../context/LanguageContext";
import CartDrawer from "./CartDrawer";
import { searchProducts, normalizeSearchQuery } from "../utils/searchApi";
import { getProductLink } from "../utils/productLink";
import { trackSearch } from "../utils/analytics";
// import HeaderNav from "./HeaderNav";

function Header({ onProfileClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const pathname = location.pathname || "/";
  const isHome = pathname === "/";
  const isShop = pathname.startsWith("/shop");
  const isContact = pathname.startsWith("/contact");
  const { count, toggle } = useCart();
  const { categories } = useCategories();
  const { items: wishlistItems } = useWishlist();
  const { language, changeLanguage } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);

  // Helper function to add language prefix to paths
  const getLocalizedPath = (path) => {
    // Split path and query string
    const [pathPart, queryString] = path.split("?");

    if (language === "ar") {
      // Remove /ar if already present to avoid duplication
      const cleanPath = pathPart.startsWith("/ar")
        ? pathPart.replace("/ar", "")
        : pathPart;
      const localizedPath = `/ar${cleanPath === "/" ? "" : cleanPath}`;
      return queryString ? `${localizedPath}?${queryString}` : localizedPath;
    }
    // Remove /ar prefix if present when switching to English
    const englishPath = pathPart.startsWith("/ar")
      ? pathPart.replace("/ar", "") || "/"
      : pathPart;
    return queryString ? `${englishPath}?${queryString}` : englishPath;
  };

  // Wishlist count from context
  const wishlistCount = wishlistItems.length;

  // Listen for localStorage changes to update wishlist count
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "wishlist_items") {
        // Force re-render by updating state
        // The WishlistContext should handle this, but we can trigger a re-check
        window.dispatchEvent(new Event("wishlist-updated"));
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom events from same window
    const handleWishlistUpdate = () => {
      // Force component to re-render by checking localStorage
      // The WishlistContext will pick up the change
    };

    window.addEventListener("wishlist-updated", handleWishlistUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("wishlist-updated", handleWishlistUpdate);
    };
  }, []);

  // Search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);
  const searchQueryRef = useRef("");

  searchQueryRef.current = searchQuery;

  const DEBOUNCE_MS = 400;

  // Debounce: after user stops typing, call API from inside the timeout (no effect cleanup = timeout always fires)
  useEffect(() => {
    const trimmed = (searchQuery || "").trim();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    if (trimmed.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      setShowSearchResults(false);
      return;
    }

    setShowSearchResults(true);
    setIsSearching(true);

    const queryToSearch = trimmed;
    searchTimeoutRef.current = setTimeout(() => {
      searchTimeoutRef.current = null;
      // Call API directly here so it always runs when the timeout fires
      searchProducts(queryToSearch, 5, language)
        .then((results) => {
          if (searchQueryRef.current.trim() === queryToSearch) {
            setSearchResults(results);
            // Track search event when search is performed
            if (results.length > 0 || queryToSearch.length >= 1) {
              trackSearch(queryToSearch);
            }
          }
          setIsSearching(false);
        })
        .catch((error) => {
          console.error("Search error:", error);
          if (searchQueryRef.current.trim() === queryToSearch) {
            setSearchResults([]);
          }
          setIsSearching(false);
        });
    }, DEBOUNCE_MS);

    // Do NOT clear the timeout in cleanup – otherwise React (e.g. Strict Mode) can clear it before it fires and the API is never called.
    return () => {};
  }, [searchQuery, language]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        searchResultsRef.current &&
        !searchInputRef.current.contains(event.target) &&
        !searchResultsRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleProfileClick = (e) => {
    e.preventDefault();
    if (user && token) {
      // User is logged in, navigate to account page
      navigate("/account");
    } else {
      // User is not logged in, open auth modal
      if (typeof onProfileClick === "function") onProfileClick();
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsCategoriesExpanded(false);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setShowSearchResults(false);
  };

  // Search input handler with debounced search
  const handleSearchInput = (value) => {
    setSearchQuery(value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const normalized = normalizeSearchQuery(searchQuery);
    if (normalized.length >= 2) {
      navigate(`/shop/default?search=${encodeURIComponent(normalized)}`);
      setShowSearchResults(false);
      setSearchQuery("");
      closeSearch();
    }
  };

  const handleSearchResultClick = (product) => {
    navigate(getProductLink(product), { state: { product } });
    setShowSearchResults(false);
    setSearchQuery("");
    closeSearch();
  };

  return (
    <div>
      {/* Custom CSS to keep profile and wishlist icons visible on mobile and mobile menu styles */}
      <style>
        {`
          /* Match header background with footer */
          #header,
          #header .main-header,
          #header .header-default {
            background-color: #e6f0ff !important;
          }
          
          /* Keep active navigation items with same light blue background */
          .header-list-categories .categories-item.active a {
            background-color: #e6f0ff !important;
          }
          
          /* Increase logo area height and logo size */
          #header .main-header {
            min-height: 45px;
            padding: 4px 0;
          }

          .logo-header .logo {
            height: 40px;
            width: auto;
            object-fit: contain;
          }

          /* Logo + Search side-by-side (when input is visible) */
          .logo-search-wrap {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 20px;
            width: 100%;
          }

          /* Medium screens: adjust search width (only when visible, i.e., 1250px+) */
          @media only screen and (min-width: 1250px) and (max-width: 1399px) {
            .search-input-container {
              max-width: 500px;
              min-width: 350px;
            }
          }


          @media only screen and (max-width: 768px) {
            #header .main-header {
              min-height: 40px;
              padding: 3px 0;
            }
            
            .logo-header .logo {
              height: 38px;
            }
          }

          @media only screen and (max-width: 575px) {
            #header .main-header {
              min-height: 35px;
              padding: 1px 0;
            }
            
            .logo-header .logo {
              height: 35px;
            }
          }

          @media only screen and (max-width: 575px) {
            .mobile-menu-toggle {
              background: var(--blue); /* match cart notification color */
              color: #ffffff;
              border-radius: 8px;
            }

            #header .nav-icon .nav-account,
            #header .nav-icon .nav-wishlist {
              display: none !important;
            }
            #header .nav-icon .nav-icon-item i {
              font-size: 14px;
            }
            .nav-icon .count-box {
              font-size: 8px;
              min-width: 11px;
              height: 11px;
              line-height: 11px;
              top: -3px;
              right: -4px;
            }
            .nav-icon.d-flex {
              gap: 4px !important;
            }
            .wrapper-header {
              align-items: center;
            }

            /* Reorder items on mobile and tablet: cart, search, language (left to right) */
            /* With justify-content-end, higher order values appear more to the left */
            #header .nav-cart {
              order: 10; /* Leftmost */
            }
            #header .nav-search {
              order: 9; /* Middle */
            }
            #header .nav-language {
              order: 1; /* Rightmost */
            }

            /* Language button (AR/EN) - exact same size as profile, wishlist on mobile */
            #header .nav-language .nav-icon-square {
              width: 36px !important;
              height: 36px !important;
              min-width: 36px !important;
              min-height: 36px !important;
              padding: 0 !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
              font-size: 0.7rem !important;
            }
          }

          /* Apply same order on tablet screens */
          @media only screen and (min-width: 576px) and (max-width: 1024px) {
            #header .nav-cart {
              order: 10; /* Leftmost */
            }
            #header .nav-search {
              order: 9; /* Middle */
            }
            #header .nav-language {
              order: 1; /* Rightmost */
            }

            /* Fix cart icon on mobile - keep blue background like search icon */
            /* Override any conflicting styles to ensure blue background */
            #header .nav-cart li a.nav-icon-item.nav-icon-square,
            #header .nav-cart .nav-icon-item.nav-icon-square,
            #header .nav-cart a.nav-icon-item.nav-icon-square {
              background-color: var(--blue) !important;
              border-color: var(--blue) !important;
              border: 2px solid var(--blue) !important;
              color: #ffffff !important;
              padding: 6px !important;
              border-radius: 8px !important;
              display: inline-flex !important;
              justify-content: center !important;
              align-items: center !important;
            }

            #header .nav-cart li a.nav-icon-item.nav-icon-square .icon,
            #header .nav-cart li a.nav-icon-item.nav-icon-square i,
            #header .nav-cart li a.nav-icon-item.nav-icon-square i.icon,
            #header .nav-cart li a.nav-icon-item.nav-icon-square i.icon-bag,
            #header .nav-cart .nav-icon-item.nav-icon-square .icon,
            #header .nav-cart .nav-icon-item.nav-icon-square i,
            #header .nav-cart a.nav-icon-item.nav-icon-square .icon,
            #header .nav-cart a.nav-icon-item.nav-icon-square i {
              color: #ffffff !important;
            }

            /* Cart with items on mobile - keep blue background */
            #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items,
            #header .nav-cart .nav-icon-item.nav-icon-square.cart-has-items,
            #header .nav-cart a.nav-icon-item.nav-icon-square.cart-has-items {
              background-color: #0e5aa7 !important;
              border-color: #0e5aa7 !important;
              border: 2px solid #0e5aa7 !important;
              color: #ffffff !important;
            }

            #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items .icon,
            #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items i,
            #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items i.icon,
            #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items i.icon-bag,
            #header .nav-cart .nav-icon-item.nav-icon-square.cart-has-items .icon,
            #header .nav-cart .nav-icon-item.nav-icon-square.cart-has-items i,
            #header .nav-cart a.nav-icon-item.nav-icon-square.cart-has-items .icon,
            #header .nav-cart a.nav-icon-item.nav-icon-square.cart-has-items i {
              color: #ffffff !important;
            }
          }

          /* Mobile Menu Styles */
          .mobile-menu-toggle {
            background: none;
            border: none;
            color: var(--main);
            cursor: pointer;
            padding: 2px;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
          }

          .mobile-menu-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 9999;
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            padding: 0;
            overflow: hidden;
            touch-action: none;
          }

          .mobile-menu {
            background: #ffffff;
            width: 300px;
            height: 100vh;
            box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
            display: flex;
            flex-direction: column;
            animation: slideInLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            border-right: 1px solid rgba(14, 90, 167, 0.1);
            overflow: hidden;
          }

          @keyframes slideInLeft {
            from {
              transform: translateX(-100%);
            }
            to {
              transform: translateX(0);
            }
          }

          .mobile-menu-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #E0E0E0;
            background: #ffffff;
            position: relative;
          }

          .mobile-menu-header .mobile-menu-account-link {
            display: flex;
            align-items: center;
            gap: 12px;
            text-decoration: none;
            color: #0e5aa7 !important;
            font-weight: 500;
            font-size: 16px;
            flex: 1;
          }

          .mobile-menu-close {
            background: transparent;
            border: none;
            color: #333;
            cursor: pointer;
            padding: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            transition: all 0.3s ease;
          }

          .mobile-menu-close:hover {
            background: rgba(0, 0, 0, 0.05);
          }

          .mobile-menu-close svg {
            width: 24px;
            height: 24px;
          }

          .mobile-menu-close svg path {
            stroke: #333;
          }


          .mobile-menu-account-icon {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: #F5F5F5;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #666;
          }

          .mobile-menu-content {
            flex: 1;
            padding: 0;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
          }

          .mobile-menu-list {
            list-style: none;
            margin: 0;
            padding: 0;
          }

          .mobile-menu-item {
            border-bottom: 1px solid #E0E0E0;
            position: relative;
            background: #ffffff;
          }

          .mobile-menu-item:last-child {
            border-bottom: none;
          }

          .mobile-menu-item a,
          .mobile-menu-item .mobile-menu-category-link {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 20px;
            text-decoration: none;
            color: #333;
            font-weight: 500;
            font-size: 16px;
            transition: all 0.2s ease;
            position: relative;
          }

          .mobile-menu-item a:hover,
          .mobile-menu-item .mobile-menu-category-link:hover {
            background: rgba(14, 90, 167, 0.05);
          }

          .mobile-menu-item.active a {
            color: #0e5aa7;
            font-weight: 600;
          }

          .mobile-menu-item-content {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1;
          }

          .mobile-menu-item-icon {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            flex-shrink: 0;
          }

          .mobile-menu-item-icon svg {
            width: 24px;
            height: 24px;
          }

          /* Icon colors - colorful bluish with variety */
          .mobile-menu-item-icon.home {
            background: #E6F0FF;
          }
          .mobile-menu-item-icon.home svg {
            stroke: #0e5aa7;
            fill: none;
          }

          .mobile-menu-item-icon.shop {
            background: #E6F0FF;
          }
          .mobile-menu-item-icon.shop svg {
            stroke: #0e5aa7;
            fill: none;
          }

          .mobile-menu-item-icon.contact {
            background: #E6F0FF;
          }
          .mobile-menu-item-icon.contact svg {
            stroke: #0e5aa7;
            fill: none;
          }

          .mobile-menu-item-icon.wishlist {
            background: #FFE6F1;
          }
          .mobile-menu-item-icon.wishlist svg {
            stroke: #D14343;
            fill: none;
          }

          .mobile-menu-item-icon.categories {
            background: #E6F0FF;
          }
          .mobile-menu-item-icon.categories svg {
            stroke: #0e5aa7;
            fill: none;
          }

          .mobile-menu-item-arrow {
            color: #666;
            font-size: 20px;
            font-weight: 300;
            transform: scaleX(1.5);
            transition: transform 0.3s ease;
          }

          .mobile-menu-item-arrow.expanded {
            transform: scaleX(1.5) rotate(90deg);
          }

          /* Categories submenu */
          .mobile-menu-categories-list {
            list-style: none;
            margin: 0;
            padding: 0;
            background: #FAFAFA;
            border-top: 1px solid #E0E0E0;
            max-height: 400px;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
          }

          .mobile-menu-categories-list li {
            border-bottom: 1px solid #E0E0E0;
          }

          .mobile-menu-categories-list li:last-child {
            border-bottom: none;
          }

          .mobile-menu-category-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 14px 20px 14px 64px;
            text-decoration: none;
            color: #0e5aa7 !important;
            font-weight: 400;
            font-size: 15px;
            transition: all 0.2s ease;
          }

          .mobile-menu-category-item span {
            color: #0e5aa7 !important;
          }

          .mobile-menu-category-item .mobile-menu-item-arrow {
            color: #0e5aa7 !important;
          }

          .mobile-menu-category-item:hover {
            background: rgba(14, 90, 167, 0.05);
            color: #0a4a8a !important;
          }

          .mobile-menu-category-item:hover span {
            color: #0a4a8a !important;
          }

          .mobile-menu-category-item:hover .mobile-menu-item-arrow {
            color: #0a4a8a !important;
          }

          /* Quick Links text styling */
          .quick-links-text {
            color: var(--main);
            font-weight: 600;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }


          /* Re-apply mobile override after base styles so it wins */
          @media only screen and (max-width: 575px) {
            .mobile-menu-toggle {
              background: var(--blue);
              color: #ffffff;
              border-radius: 8px;
            }
            
            /* Center logo in leftmost area after hamburger menu on mobile */
            .wrapper-header .col-md-4.col-3:first-of-type {
              flex: 0 0 auto;
              max-width: auto;
            }
            
            .wrapper-header .col-md-4.col-6 {
              flex: 1 1 auto;
              display: flex;
              justify-content: center;
              align-items: center;
              padding-left: 0;
            }
            
            .wrapper-header .logo-search-wrap {
              justify-content: center;
              width: 100%;
            }
            
            .wrapper-header .logo-header {
              margin: 0 auto;
            }
          }

          /* Cart icon bluish filled when items are in cart */
          .nav-cart .nav-icon-item.cart-has-items {
            background-color: #0e5aa7 !important;
            color: #ffffff !important;
          }

          .nav-cart .nav-icon-item.cart-has-items .icon,
          .nav-cart .nav-icon-item.cart-has-items .icon:before {
            color: #ffffff !important;
          }

          /* Fix hover effect for header icons - keep icons visible */
          #header .nav-icon .nav-icon-item:hover {
            background-color: rgba(14, 90, 167, 0.1) !important;
            color: #0e5aa7 !important;
          }

          #header .nav-icon .nav-icon-item:hover .icon,
          #header .nav-icon .nav-icon-item:hover .icon:before {
            color: #0e5aa7 !important;
          }

          /* Ensure icons remain visible on hover - no white color */
          #header .nav-icon .nav-icon-item:hover i,
          #header .nav-icon .nav-icon-item:hover i:before {
            color: #0e5aa7 !important;
          }

          /* Profile, wishlist icons - keep original color on hover */
          #header .nav-account .nav-icon-item:hover,
          #header .nav-wishlist .nav-icon-item:hover {
            background-color: rgba(14, 90, 167, 0.1) !important;
          }

          #header .nav-account .nav-icon-item:hover .icon,
          #header .nav-wishlist .nav-icon-item:hover .icon {
            color: #0e5aa7 !important;
          }

          /* Language selector (AR/EN) - exact same size as profile, wishlist */
          #header .nav-language .nav-icon-square {
            background-color: var(--blue) !important;
            border: 2px solid var(--blue) !important;
            color: #ffffff !important;
            padding: 0 !important;
            font-weight: 600;
            font-size: 0.7rem;
            border-radius: 8px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 36px !important;
            height: 36px !important;
            min-width: 36px !important;
            min-height: 36px !important;
            box-sizing: border-box !important;
          }

          #header .nav-language .nav-icon-square span {
            color: #ffffff !important;
          }

          #header .nav-language button.nav-icon-square {
            cursor: pointer;
            font-family: inherit;
          }

          #header .nav-language .nav-icon-item:hover {
            background-color: rgba(14, 90, 167, 0.9) !important;
          }

          #header .nav-language .nav-icon-item:hover span {
            color: #ffffff !important;
          }

          /* Cart icon - match profile and wishlist icons with bluish background */
          @media (min-width: 576px) {
            #header .nav-cart .nav-icon-square:not(.cart-has-items) {
              background-color: var(--blue) !important;
              border-color: var(--blue) !important;
              color: #ffffff !important;
            }

            #header .nav-cart .nav-icon-square:not(.cart-has-items) .icon,
            #header .nav-cart .nav-icon-square:not(.cart-has-items) i {
              color: #ffffff !important;
            }
          }

          /* Cart icon hover - override all conflicting styles */
          #header .nav-cart li a.nav-icon-item.nav-icon-square:not(.cart-has-items):hover {
            background-color: rgba(14, 90, 167, 0.1) !important;
            border-color: rgba(14, 90, 167, 0.2) !important;
            color: #0e5aa7 !important;
          }

          #header .nav-cart li a.nav-icon-item.nav-icon-square:not(.cart-has-items):hover .icon,
          #header .nav-cart li a.nav-icon-item.nav-icon-square:not(.cart-has-items):hover .icon:before,
          #header .nav-cart li a.nav-icon-item.nav-icon-square:not(.cart-has-items):hover i,
          #header .nav-cart li a.nav-icon-item.nav-icon-square:not(.cart-has-items):hover i.icon,
          #header .nav-cart li a.nav-icon-item.nav-icon-square:not(.cart-has-items):hover i.icon-bag {
            color: #0e5aa7 !important;
          }

          /* Cart with items - keep blue background and white icon on hover */
          #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items:hover {
            background-color: #0e5aa7 !important;
            border-color: #0e5aa7 !important;
            color: #ffffff !important;
          }

          #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items:hover .icon,
          #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items:hover .icon:before,
          #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items:hover i,
          #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items:hover i.icon,
          #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items:hover i.icon-bag,
          #header .nav-cart li a.nav-icon-item.nav-icon-square.cart-has-items:hover i:before {
            color: #ffffff !important;
          }

          /* Floating WhatsApp button - always visible bottom-left */
          .whatsapp-fab {
            position: fixed;
            left: 16px;
            bottom: 16px;
            width: 56px;
            height: 56px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #25d366; /* WhatsApp green */
            border-radius: 50%;
            box-shadow: 0 8px 20px rgba(0,0,0,0.15);
            color: #ffffff; /* icon color */
            z-index: 9999;
            text-decoration: none;
          }
          .whatsapp-fab i { font-size: 22px; line-height: 1; display: block; color: #ffffff; }

/* Search Input Styles - Large Screens Only */
          .search-input-container {
            display: none; /* Hidden by default (mobile) */
            align-items: center;
            position: relative;
            width: 100%;
            max-width: 800px;
            flex: 1 1 auto;
            min-width: 400px;
          }

          .search-input-container input {
            width: 100%;
            padding: 6px 45px 6px 15px;
            border: none;
            border-radius: 25px;
            font-size: 13px;
            transition: all 0.3s ease;
            background: #0e5aa7;
            color: #ffffff;
            font-weight: 400;
            height: 38px;
          }

          .search-input-container input::placeholder {
            color: rgba(255, 255, 255, 0.8);
            font-weight: 400;
          }

          .search-input-container input:focus {
            outline: none;
            background: #0c4e94;
            box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .search-input-container button {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(255, 255, 255, 0.25);
            color: #ffffff;
            border: none;
            border-radius: 50%;
            width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            backdrop-filter: blur(10px);
          }

          .search-input-container button:hover {
            background: rgba(255, 255, 255, 0.35);
            transform: translateY(-50%) scale(1.08);
          }

          .search-input-container button i {
            font-size: 14px;
          }

          /* Mobile Search Icon - Small Screens Only */
          .mobile-search-icon {
            display: block; /* Visible by default (mobile) */
          }

          /* Large Screens (1250px and above) */
          @media only screen and (min-width: 1250px) {
            .search-input-container {
              display: flex; /* Show search input */
            }
            .mobile-search-icon {
              display: none !important; /* Hide icon */
            }
          }

          /* Small Screens (below 1250px) */
          @media only screen and (max-width: 1249px) {
            .search-input-container {
              display: none !important; /* Hide search input */
            }
            .mobile-search-icon {
              display: block; /* Show icon */
            }
          }


          /* منع اللوجو من الـ shrink داخل الـ flex */
.logo-search-wrap {
  flex-wrap: nowrap;
}

.logo-header {
  flex: 0 0 auto;      /* لا يتمدد ولا يصغر */
  flex-shrink: 0;
}

.logo-header .logo {
  flex-shrink: 0;
  max-width: none;     /* لو عندك global img { max-width: 100% } */
  width: auto;
}

/* خلي البحث هو اللي يضحي بالمساحة مش اللوجو */
.search-input-container {
  flex: 1 1 auto;
  min-width: 400px;
  max-width: 800px;
}

/* Make navigation items (Home, Shop, Contact) smaller */
.header-list-categories .categories-item a {
  font-size: 12px !important;
}

.mobile-menu-item a {
  font-size: 14px !important;
}


        `}
      </style>
      {/* header */}
      <header id="header" className="header-default header-style-2">
        <div className="main-header line">
          <div className="container-full px_15 lg-px_40">
            <div className="row wrapper-header align-items-center">
              <div className="col-xl-4 tf-md-hidden">
                <ul className="header-list-categories">
                  <li className={`categories-item${isHome ? " active" : ""}`}>
                    <Link to={getLocalizedPath("/")} className="text-uppercase">
                      {language === "ar" ? "الرئيسية" : "Home"}
                    </Link>
                  </li>
                  <li className={`categories-item${isShop ? " active" : ""}`}>
                    <Link
                      to={getLocalizedPath("/shop/collection?category_id=1563")}
                      className="text-uppercase"
                    >
                      {language === "ar" ? "المتجر" : "Shop"}
                    </Link>
                  </li>
                  <li
                    className={`categories-item${isContact ? " active" : ""}`}
                  >
                    <Link
                      to={getLocalizedPath("/contact")}
                      className="text-uppercase"
                    >
                      {language === "ar" ? "اتصل بنا" : "Contact"}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="col-md-4 col-3 tf-lg-hidden">
                <button
                  onClick={toggleMobileMenu}
                  className="mobile-menu-toggle"
                  aria-label="Toggle mobile menu"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="16"
                    viewBox="0 0 24 16"
                    fill="none"
                  >
                    <path
                      d="M2.00056 2.28571H16.8577C17.1608 2.28571 17.4515 2.16531 17.6658 1.95098C17.8802 1.73665 18.0006 1.44596 18.0006 1.14286C18.0006 0.839753 17.8802 0.549063 17.6658 0.334735C17.4515 0.120408 17.1608 0 16.8577 0H2.00056C1.69745 0 1.40676 0.120408 1.19244 0.334735C0.978109 0.549063 0.857702 0.839753 0.857702 1.14286C0.857702 1.44596 0.978109 1.73665 1.19244 1.95098C1.40676 2.16531 1.69745 2.28571 2.00056 2.28571ZM0.857702 8C0.857702 7.6969 0.978109 7.40621 1.19244 7.19188C1.40676 6.97755 1.69745 6.85714 2.00056 6.85714H22.572C22.8751 6.85714 23.1658 6.97755 23.3801 7.19188C23.5944 7.40621 23.7148 7.6969 23.7148 8C23.7148 8.30311 23.5944 8.59379 23.3801 8.80812C23.1658 9.02245 22.8751 9.14286 22.572 9.14286H2.00056C1.69745 9.14286 1.40676 9.02245 1.19244 8.80812C0.978109 8.59379 0.857702 8.30311 0.857702 8ZM0.857702 14.8571C0.857702 14.554 0.978109 14.2633 1.19244 14.049C1.40676 13.8347 1.69745 13.7143 2.00056 13.7143H12.2863C12.5894 13.7143 12.8801 13.8347 13.0944 14.049C13.3087 14.2633 13.4291 14.554 13.4291 14.8571C13.4291 15.1602 13.3087 15.4509 13.0944 15.6653C12.8801 15.8796 12.5894 16 12.2863 16H2.00056C1.69745 16 1.40676 15.8796 1.19244 15.6653C0.978109 15.4509 0.857702 15.1602 0.857702 14.8571Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </button>
              </div>
              <div className="col-xl-4 col-md-4 col-6">
                <div className="logo-search-wrap">
                  <Link to="/" className="logo-header">
                    <img
                      src="/assets/images/logo/EL-SALLAB-LOGO-w-colors-2.png"
                      alt="logo"
                      className="logo"
                    />
                  </Link>

                  {/* Desktop/Tablet search input (beside logo) */}
                  <div style={{ position: "relative", width: "100%" }}>
                    <form
                      className="search-input-container header-search-input"
                      onSubmit={handleSearchSubmit}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={
                          language === "ar"
                            ? "ابحث عما تحتاج..."
                            : "Search what you need..."
                        }
                        value={searchQuery}
                        onChange={(e) => handleSearchInput(e.target.value)}
                        onCompositionEnd={(e) => {
                          // Sync final value when IME composition ends (e.g. Arabic) so debounce runs with committed text
                          handleSearchInput(e.target.value);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (searchQuery.trim().length >= 2) {
                            setShowSearchResults(true);
                          }
                        }}
                        onFocus={() => {
                          if (
                            searchQuery.trim().length >= 2 &&
                            searchResults.length > 0
                          ) {
                            setShowSearchResults(true);
                          }
                        }}
                      />
                      <button className="" type="submit" aria-label="Search">
                        <i className="icon-search"></i>
                      </button>
                    </form>

                    {/* Search results dropdown */}
                    {showSearchResults && searchQuery.trim().length >= 2 && (
                      <div
                        ref={searchResultsRef}
                        className="search-results-dropdown"
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          backgroundColor: "#ffffff",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          marginTop: "4px",
                          maxHeight: "400px",
                          overflowY: "auto",
                          zIndex: 1000,
                        }}
                      >
                        {isSearching ? (
                          <div
                            style={{
                              padding: "20px",
                              textAlign: "center",
                              color: "#666",
                            }}
                          >
                            {language === "ar"
                              ? "جاري البحث..."
                              : "Searching..."}
                          </div>
                        ) : searchResults.length > 0 ? (
                          <>
                            {searchResults.map((product) => (
                              <Link
                                key={product.id}
                                to={getProductLink(product)}
                                state={{ product }}
                                onClick={() => handleSearchResultClick(product)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "12px 16px",
                                  textDecoration: "none",
                                  color: "#333",
                                  borderBottom: "1px solid #f0f0f0",
                                  transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#f5f5f5";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                              >
                                <img
                                  src={
                                    product.image ||
                                    `https://erp-elsalab.odoo.com/web/image/${product.id}`
                                  }
                                  alt={product.name}
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    objectFit: "cover",
                                    borderRadius: "4px",
                                    marginRight: "12px",
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontWeight: 500,
                                      fontSize: "14px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    {product.name}
                                  </div>
                                  {product.price && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#0e5aa7",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {product.price.toLocaleString()}{" "}
                                      {language === "ar" ? "ج.م" : "EGP"}
                                    </div>
                                  )}
                                </div>
                              </Link>
                            ))}
                            <Link
                              to={`/shop/default?search=${encodeURIComponent(normalizeSearchQuery(searchQuery))}`}
                              onClick={() => {
                                setShowSearchResults(false);
                                setSearchQuery("");
                              }}
                              style={{
                                display: "block",
                                padding: "12px 16px",
                                textAlign: "center",
                                textDecoration: "none",
                                color: "#0e5aa7",
                                fontWeight: 500,
                                borderTop: "2px solid #f0f0f0",
                                backgroundColor: "#f9f9f9",
                              }}
                            >
                              {language === "ar"
                                ? `عرض جميع النتائج (${searchResults.length}+)`
                                : `View all results (${searchResults.length}+)`}
                            </Link>
                          </>
                        ) : (
                          <div
                            style={{
                              padding: "20px",
                              textAlign: "center",
                              color: "#666",
                            }}
                          >
                            {language === "ar"
                              ? "لا توجد نتائج"
                              : "No results found"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-xl-4 col-md-4 col-3">
                <ul className="nav-icon d-flex justify-content-end align-items-center gap-20">
                  <li className="nav-search">
                    {/* الأيقونة تظهر فقط في الشاشات الصغيرة */}
                    <a
                      href="#"
                      className="nav-icon-item nav-icon-square mobile-search-icon"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleSearch();
                      }}
                      aria-label="Open search"
                    >
                      <i className="icon icon-search"></i>
                    </a>
                  </li>

                  <li className="nav-language">
                    <button
                      type="button"
                      className="nav-icon-item nav-icon-square"
                      onClick={(e) => {
                        e.preventDefault();
                        changeLanguage(language === "en" ? "ar" : "en");
                      }}
                      aria-label={
                        language === "en"
                          ? "Switch to Arabic"
                          : "Switch to English"
                      }
                    >
                      <span>{language === "ar" ? "EN" : "AR"}</span>
                    </button>
                  </li>

                  <li className="nav-account">
                    <a
                      href="#"
                      className="nav-icon-item nav-icon-square"
                      onClick={handleProfileClick}
                      role="button"
                      aria-label="Open account modal"
                    >
                      <i className="icon icon-account"></i>
                    </a>
                  </li>
                  <li className="nav-wishlist">
                    <Link
                      to="/account/wishlist"
                      className="nav-icon-item nav-icon-square"
                      aria-label="Open wishlist"
                    >
                      <i className="icon icon-heart"></i>
                      {wishlistCount > 0 && (
                        <span className="count-box">{wishlistCount}</span>
                      )}
                    </Link>
                  </li>
                  <li className="nav-cart">
                    <a
                      href="#"
                      className={`nav-icon-item nav-icon-square ${
                        count > 0 ? "cart-has-items" : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        toggle();
                      }}
                    >
                      <i className="icon icon-bag"></i>
                      <span className="count-box">{count}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        {/* <HeaderNav /> */}
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <Link
                to="/account"
                onClick={closeMobileMenu}
                className="mobile-menu-account-link"
              >
                <div className="mobile-menu-account-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 14C7.58172 14 4 16.6863 4 20V22H20V20C20 16.6863 16.4183 14 12 14Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <span>My Account</span>
              </Link>
              <button
                onClick={closeMobileMenu}
                className="mobile-menu-close"
                aria-label="Close menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="mobile-menu-content">
              <ul className="mobile-menu-list">
                <li className={`mobile-menu-item${isHome ? " active" : ""}`}>
                  <Link to={getLocalizedPath("/")} onClick={closeMobileMenu}>
                    <div className="mobile-menu-item-content">
                      <div className="mobile-menu-item-icon home">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9 22V12H15V22"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span>{language === "ar" ? "الرئيسية" : "Home"}</span>
                    </div>
                    <span className="mobile-menu-item-arrow">›</span>
                  </Link>
                </li>
                <li className={`mobile-menu-item${isShop ? " active" : ""}`}>
                  <Link
                    to={getLocalizedPath("/shop/collection?category_id=1563")}
                    onClick={closeMobileMenu}
                  >
                    <div className="mobile-menu-item-content">
                      <div className="mobile-menu-item-icon shop">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H19M17 13V17C17 17.5304 17.2107 18.0391 17.5858 18.4142C17.9609 18.7893 18.4696 19 19 19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V13"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span>{language === "ar" ? "المتجر" : "Shop"}</span>
                    </div>
                    <span className="mobile-menu-item-arrow">›</span>
                  </Link>
                </li>
                <li className="mobile-menu-item">
                  <div
                    onClick={() =>
                      setIsCategoriesExpanded(!isCategoriesExpanded)
                    }
                    className="mobile-menu-category-link"
                    style={{ cursor: "pointer" }}
                  >
                    <div className="mobile-menu-item-content">
                      <div className="mobile-menu-item-icon categories">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="7"
                            height="7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <rect
                            x="14"
                            y="3"
                            width="7"
                            height="7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <rect
                            x="3"
                            y="14"
                            width="7"
                            height="7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <rect
                            x="14"
                            y="14"
                            width="7"
                            height="7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span>Categories</span>
                    </div>
                    <span
                      className={`mobile-menu-item-arrow ${
                        isCategoriesExpanded ? "expanded" : ""
                      }`}
                    >
                      ›
                    </span>
                  </div>
                  {isCategoriesExpanded && (
                    <ul className="mobile-menu-categories-list">
                      {(categories || []).slice(0, 11).map((category) => (
                        <li key={category.id || category.name}>
                          <Link
                            to={
                              category.id
                                ? `/shop/collection?category_id=${category.id}`
                                : category.link || "/shop/collection"
                            }
                            onClick={closeMobileMenu}
                            className="mobile-menu-category-item"
                          >
                            <span>{category.name}</span>
                            <span className="mobile-menu-item-arrow">›</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
                <li className={`mobile-menu-item${isContact ? " active" : ""}`}>
                  <Link
                    to={getLocalizedPath("/contact")}
                    onClick={closeMobileMenu}
                  >
                    <div className="mobile-menu-item-content">
                      <div className="mobile-menu-item-icon contact">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span>{language === "ar" ? "اتصل بنا" : "Contact"}</span>
                    </div>
                    <span className="mobile-menu-item-arrow">›</span>
                  </Link>
                </li>
                <li className="mobile-menu-item">
                  <Link to="/account/wishlist" onClick={closeMobileMenu}>
                    <div className="mobile-menu-item-content">
                      <div className="mobile-menu-item-icon wishlist">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span>Wishlist</span>
                    </div>
                    <span className="mobile-menu-item-arrow">›</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Search Sidebar - uses same searchQuery state and debounced search effect as header (single source of truth) */}
      {isSearchOpen && (
        <div
          className="offcanvas-backdrop fade show"
          onClick={closeSearch}
        ></div>
      )}
      <div
        className={`offcanvas offcanvas-end canvas-search ${
          isSearchOpen ? "show" : ""
        }`}
        style={{ visibility: isSearchOpen ? "visible" : "hidden" }}
      >
        <div className="canvas-wrapper">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">
              {language === "ar" ? "ابحث في موقعنا" : "Search our site"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={closeSearch}
            ></button>
          </div>
          <header className="tf-search-head">
            <div className="tf-search-sticky">
              <form
                className="tf-mini-search-frm"
                onSubmit={handleSearchSubmit}
              >
                <fieldset className="text">
                  <input
                    type="text"
                    placeholder={
                      language === "ar"
                        ? "ابحث عما تحتاج..."
                        : "Search what you need..."
                    }
                    className=""
                    name="text"
                    tabIndex="0"
                    value={searchQuery}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    onCompositionEnd={(e) => {
                      // Sync final value when IME composition ends (e.g. Arabic) so debounce runs with committed text
                      handleSearchInput(e.target.value);
                    }}
                    autoComplete="off"
                    onFocus={() => {
                      if (
                        searchQuery.trim().length >= 2 &&
                        searchResults.length > 0
                      ) {
                        setShowSearchResults(true);
                      }
                    }}
                    aria-required="true"
                    required
                  />
                </fieldset>
              </form>
            </div>
          </header>
          <div className="canvas-body p-0">
            <div className="tf-search-content">
              {/* Show search results when searching */}
              {searchQuery.trim().length >= 2 ? (
                <div style={{ padding: "20px" }}>
                  {isSearching ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#666",
                      }}
                    >
                      {language === "ar" ? "جاري البحث..." : "Searching..."}
                    </div>
                  ) : searchResults.length > 0 ? (
                    <>
                      <div
                        style={{
                          marginBottom: "20px",
                          fontWeight: 600,
                          fontSize: "16px",
                          color: "#333",
                        }}
                      >
                        {language === "ar" ? "نتائج البحث" : "Search Results"}
                      </div>
                      <div
                        style={{
                          maxHeight: "calc(100vh - 250px)",
                          overflowY: "auto",
                        }}
                      >
                        {searchResults.map((product) => (
                          <Link
                            key={product.id}
                            to={getProductLink(product)}
                            state={{ product }}
                            onClick={() => handleSearchResultClick(product)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "16px",
                              textDecoration: "none",
                              color: "#333",
                              borderBottom: "1px solid #f0f0f0",
                              transition: "background-color 0.2s",
                              borderRadius: "8px",
                              marginBottom: "8px",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#f5f5f5";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }}
                          >
                            <img
                              src={
                                product.image ||
                                `https://erp-elsalab.odoo.com/web/image/${product.id}`
                              }
                              alt={product.name}
                              style={{
                                width: "60px",
                                height: "60px",
                                objectFit: "cover",
                                borderRadius: "4px",
                                marginRight: "16px",
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: 500,
                                  fontSize: "15px",
                                  marginBottom: "6px",
                                }}
                              >
                                {product.name}
                              </div>
                              {product.price && (
                                <div
                                  style={{
                                    fontSize: "14px",
                                    color: "#0e5aa7",
                                    fontWeight: 600,
                                  }}
                                >
                                  {product.price.toLocaleString()}{" "}
                                  {language === "ar" ? "ج.م" : "EGP"}
                                </div>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                      <Link
                        to={`/shop/default?search=${encodeURIComponent(normalizeSearchQuery(searchQuery))}`}
                        onClick={() => {
                          setShowSearchResults(false);
                          setSearchQuery("");
                          closeSearch();
                        }}
                        style={{
                          display: "block",
                          padding: "16px",
                          textAlign: "center",
                          textDecoration: "none",
                          color: "#0e5aa7",
                          fontWeight: 600,
                          borderTop: "2px solid #f0f0f0",
                          backgroundColor: "#f9f9f9",
                          borderRadius: "8px",
                          marginTop: "20px",
                        }}
                      >
                        {language === "ar"
                          ? `عرض جميع النتائج (${searchResults.length}+)`
                          : `View all results (${searchResults.length}+)`}
                      </Link>
                    </>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#666",
                      }}
                    >
                      {language === "ar" ? "لا توجد نتائج" : "No results found"}
                    </div>
                  )}
                </div>
              ) : (
                /* Show quick links when not searching */
                <div className="tf-cart-hide-has-results">
                  <div className="tf-col-quicklink">
                    <div className="tf-search-content-title fw-5">
                      {language === "ar" ? "روابط سريعة" : "Quick link"}
                    </div>
                    <ul className="tf-quicklink-list">
                      <li className="tf-quicklink-item">
                        <br />
                        <span className="quick-links-text">
                          {language === "ar" ? "روابط سريعة" : "Quick Links"}
                        </span>
                      </li>
                      {categories && categories.length > 0 ? (
                        categories.map((category) => (
                          <li key={category.id} className="tf-quicklink-item">
                            <Link
                              to={`/shop/default?category_id=${category.id}`}
                              onClick={closeSearch}
                            >
                              {category.name}
                            </Link>
                          </li>
                        ))
                      ) : (
                        // Fallback categories if API fails
                        <>
                          <li className="tf-quicklink-item">
                            <Link to="/shop/default" onClick={closeSearch}>
                              {language === "ar" ? "بلاط" : "Tiles"}
                            </Link>
                          </li>
                          <li className="tf-quicklink-item">
                            <Link to="/shop/default" onClick={closeSearch}>
                              {language === "ar"
                                ? "أدوات صحية"
                                : "Sanitary Ware"}
                            </Link>
                          </li>
                          <li className="tf-quicklink-item">
                            <Link to="/shop/default" onClick={closeSearch}>
                              {language === "ar" ? "خلاطات" : "Mixers"}
                            </Link>
                          </li>
                          <li className="tf-quicklink-item">
                            <Link to="/shop/default" onClick={closeSearch}>
                              {language === "ar" ? "اكسسوارات" : "Accessories"}
                            </Link>
                          </li>
                          <li className="tf-quicklink-item">
                            <Link to="/shop/default" onClick={closeSearch}>
                              {language === "ar" ? "حمام" : "Bathroom"}
                            </Link>
                          </li>
                          <li className="tf-quicklink-item">
                            <Link to="/shop/default" onClick={closeSearch}>
                              {language === "ar" ? "مطبخ" : "Kitchen"}
                            </Link>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CartDrawer />

      {/* Floating WhatsApp button */}
      <a
        href="https://wa.me/201234567890"
        className="whatsapp-fab"
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Chat with us on WhatsApp"
      >
        <i className="icon icon-whatsapp"></i>
      </a>
    </div>
  );
}

export default Header;
